import { type Dtos, suretyBondFormTemplateId } from "@inrev/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { EMPTY_BOND_FORM } from "../../../constants";
import type { BondFormData } from "../../../types";

export type BondFormUpdate = Dtos.SuretyBondForm.Update.Request;

export type BondFormSubmitCallbacks = {
	onSuccess?: () => void;
	onError: (error: Error) => void;
};

const isSameBondForm = (a: BondFormData, b: BondFormData) =>
	a.templateId === b.templateId &&
	a.upload.type === b.upload.type &&
	a.upload.files.length === b.upload.files.length &&
	a.upload.files.every((file, index) => file.id === b.upload.files[index]?.id);

export const toBondFormRequestPayload = (bondForm: BondFormData): BondFormUpdate => {
	if (bondForm.templateId) {
		return {
			templateId: suretyBondFormTemplateId.parse(bondForm.templateId),
			bondFormType: null,
			uploadedFileIds: [],
		};
	}
	const uploadType = bondForm.upload.type;
	const [firstFileId, ...restFileIds] = bondForm.upload.files.map((file) => file.id);
	if (uploadType !== "" && firstFileId) {
		return {
			templateId: null,
			uploadedFileIds: [firstFileId, ...restFileIds],
			bondFormType: uploadType,
		};
	}
	return { templateId: null, bondFormType: null, uploadedFileIds: [] };
};

type Options = {
	savedBondForm: BondFormData | undefined;
	submit: (update: BondFormUpdate, callbacks: BondFormSubmitCallbacks) => void;
};

export type UseBondFormResult = {
	value: BondFormData;
	onChange: (next: BondFormData) => void;
};

export const useBondForm = ({ savedBondForm, submit }: Options): UseBondFormResult => {
	const [value, setValue] = useState<BondFormData>(savedBondForm ?? EMPTY_BOND_FORM);
	/** Last value the server confirmed — the target a failed PUT reverts to. */
	const lastSavedRef = useRef<BondFormData>(savedBondForm ?? EMPTY_BOND_FORM);
	/** The saved value the local state was last seeded from, compared structurally so an
	 * unmemoized `savedBondForm` can't overwrite the working copy on every render. */
	const syncedSavedRef = useRef<BondFormData | undefined>(savedBondForm);
	/** Bumped per submission so a stale response can't overwrite a newer draft. */
	const submissionRef = useRef(0);
	/** Highest submission id whose PUT succeeded — keeps lastSavedRef from moving backwards
	 * when an older request resolves after a newer one. */
	const lastSuccessIdRef = useRef(0);
	const submitRef = useRef(submit);
	submitRef.current = submit;

	useEffect(() => {
		if (!savedBondForm) return;
		const synced = syncedSavedRef.current;
		if (synced && isSameBondForm(synced, savedBondForm)) return;
		lastSavedRef.current = savedBondForm;
		syncedSavedRef.current = savedBondForm;
		setValue(savedBondForm);
	}, [savedBondForm]);

	const onChange = useCallback((next: BondFormData) => {
		const submissionId = ++submissionRef.current;
		setValue(next);
		submitRef.current(toBondFormRequestPayload(next), {
			onSuccess: () => {
				if (submissionId < lastSuccessIdRef.current) return;
				lastSuccessIdRef.current = submissionId;
				lastSavedRef.current = next;
			},
			onError: () => {
				if (submissionRef.current !== submissionId) return;
				setValue(lastSavedRef.current);
			},
		});
	}, []);

	return { value, onChange };
};
